/**
 * AgeGateModal — one-time 18+ confirmation.
 *
 * Two entry points:
 *  1. Default export <AgeGateModal/> — first-launch gate on the AuthPage
 *     (localStorage flag). Shown to EVERY visitor (login / register / guest)
 *     before use. On confirm it persists the flag locally AND, best-effort,
 *     records the attestation server-side via POST /api/auth/confirm-age
 *     (a no-op 401 for a pre-login visitor — account-creation records it).
 *  2. <AgeGateHost/> — mounted once at the app root. Listens for the
 *     `fws:age-required` window event fired by the axios 403 interceptor
 *     (src/lib/api.js) when the backend's RequireAgeConfirmedMiddleware
 *     rejects an unconfirmed account. A no-dismiss modal whose confirm calls
 *     /api/auth/confirm-age and reloads so the blocked request retries.
 */
import { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { authAPI } from '../lib/api';

const STORAGE_KEY = 'fws_age_confirmed';

function alreadyConfirmed() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function persistLocal() {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    /* ignore — localStorage can be disabled (Safari private mode) */
  }
}

function AgeGateBody({ onConfirm, busy }) {
  const [blocked, setBlocked] = useState(false);
  const dialogRef = useRef(null);

  // WCAG 2.1.2/2.4.3 — trap keyboard focus inside the gate so a user can't Tab
  // out into the (inert) AuthPage behind it, and move initial focus to the
  // first action. (2026-06-17 a11y audit P1 missing-focus-trap.)
  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return undefined;
    const focusables = () => Array.from(node.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )).filter((el) => !el.disabled && el.offsetParent !== null);
    // Initial focus on the primary action.
    const first = focusables()[0];
    if (first) first.focus();
    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const els = focusables();
      if (els.length === 0) return;
      const firstEl = els[0];
      const lastEl = els[els.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    node.addEventListener('keydown', onKeyDown);
    return () => node.removeEventListener('keydown', onKeyDown);
  }, [blocked]);

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-[100] flex items-center justify-center px-6 bg-background/90 backdrop-blur-md"
      data-testid="age-gate-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
    >
      <div className="w-full max-w-sm rounded-2xl border border-border/60 bg-card p-6 text-center shadow-2xl">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Sparkles className="h-7 w-7 text-primary" />
        </div>

        {!blocked ? (
          <>
            <h2
              id="age-gate-title"
              className="text-xl font-bold mb-2"
              style={{ fontFamily: 'var(--font-heading)' }}
              data-testid="age-gate-title"
            >
              Quick check before you take the stage
            </h2>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              Fantasy World Star is for adults — confirm you're 18 or older
              to start drafting the world's stars.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={onConfirm} disabled={busy} className="w-full" data-testid="age-gate-confirm-button">
                {busy ? 'Confirming…' : "I'm 18+ — Let's play"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setBlocked(true)}
                disabled={busy}
                className="w-full text-muted-foreground"
                data-testid="age-gate-decline-button"
              >
                I'm under 18
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }} data-testid="age-gate-blocked-title">
              This stage is 18+
            </h2>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              Fantasy World Star is only for players 18 and older. Thanks for
              understanding — we'd love to see you back when you're of age.
            </p>
            <Button
              variant="outline"
              onClick={() => setBlocked(false)}
              className="w-full"
              data-testid="age-gate-back-button"
            >
              Go back
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function AgeGateModal() {
  const [show, setShow] = useState(() => !alreadyConfirmed());
  const [busy, setBusy] = useState(false);

  if (!show) return null;

  const confirm = async () => {
    persistLocal();
    setBusy(true);
    // RC audit: no server call here — this modal only renders on the
    // PRE-LOGIN AuthPage where POST /auth/confirm-age can only 401 (noise).
    // Account creation persists age_confirmed=True server-side, and the
    // authed AgeGateHost below handles legacy unconfirmed accounts.
    setBusy(false);
    setShow(false);
  };

  return <AgeGateBody onConfirm={confirm} busy={busy} />;
}

/**
 * Top-level mount that listens to the `fws:age-required` event and shows a
 * no-dismiss 18+ confirmation. Drop this once near the app root. Only ever
 * fires for an authenticated account the backend reports as unconfirmed.
 */
export function AgeGateHost() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = () => setShow(true);
    window.addEventListener('fws:age-required', handler);
    return () => window.removeEventListener('fws:age-required', handler);
  }, []);

  if (!show) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await authAPI.confirmAge();
      persistLocal();
      // 2026-08-31 (age-gate soften, level 2) — no more full page reload.
      // Continue in place: the backend now bootstraps teams at confirm-age,
      // and fws:team-refresh re-pulls whatever request the 403 blocked.
      setShow(false);
      try { window.dispatchEvent(new CustomEvent('fws:team-refresh')); } catch { /* noop */ }
      setBusy(false);
    } catch {
      setBusy(false);
    }
  };

  return <AgeGateBody onConfirm={confirm} busy={busy} />;
}
